/**
 * Who is starting and who is leaving, by week.
 *
 * Read from the work: an onboarding's start date and an offboarding's last
 * day, not cancelled, and not an access change (that person is staying). HR
 * sees this week and next on one screen, and the departments that have to
 * prepare (IT and Facilities, by default) are mailed next week's before the
 * cards for it arrive, so a laptop is not ordered on the Monday it is needed.
 * A week is Monday to Sunday.
 */

import {
  addCalendarDays,
  atTime,
  compareDates,
  isBusinessDay,
  weekday,
  type Calendar,
  type IsoDate,
} from "./dates.js";
import { isAccessChange } from "./employees.js";
import type { Instance } from "./instance.js";

/** Told next week's starters and leavers unless a level says otherwise. */
export const DEFAULT_PREVIEW_DEPARTMENTS: readonly string[] = ["IT", "Facilities"];

export const MAX_PREVIEW_DEPARTMENTS = 20;

/** One person starting or leaving, with the work that says so. */
export interface Mover {
  instanceId: string;
  name: string;
  date: IsoDate;
  department: string;
  /** An offboarding's reason; empty for a start. */
  trigger: string;
}

export interface WeekPlan {
  /** The Monday. */
  start: IsoDate;
  /** The Sunday. */
  end: IsoDate;
  starting: Mover[];
  leaving: Mover[];
}

/** The work a week is read from: an instance and the row facts it does not carry itself. */
export interface WeekEntry {
  instanceId: string;
  name: string;
  anchorDate: IsoDate;
  instance: Pick<Instance, "kind" | "cancelled" | "profile">;
}

/** The Monday of the week `date` is in. */
export const weekStart = (date: IsoDate): IsoDate => addCalendarDays(date, -((weekday(date) + 6) % 7));

export function weekPlan(entries: readonly WeekEntry[], start: IsoDate): WeekPlan {
  const end = addCalendarDays(start, 6);
  const inside = (date: IsoDate) => compareDates(date, start) >= 0 && compareDates(date, end) <= 0;
  const movers = (kind: "onboarding" | "offboarding"): Mover[] =>
    entries
      .filter(
        ({ instance, anchorDate }) =>
          instance.kind === kind && !instance.cancelled && !isAccessChange(instance) && inside(anchorDate),
      )
      .map(({ instanceId, name, anchorDate, instance }) => ({
        instanceId,
        name,
        date: anchorDate,
        department: instance.profile.department,
        trigger: instance.profile.trigger,
      }))
      .sort((a, b) => compareDates(a.date, b.date) || a.name.localeCompare(b.name));
  return { start, end, starting: movers("onboarding"), leaving: movers("offboarding") };
}

/** This week's and next week's, as the calendar stands on `today` at one site. */
export function weeksAround(
  entries: readonly WeekEntry[],
  today: IsoDate,
): { thisWeek: WeekPlan; nextWeek: WeekPlan } {
  const start = weekStart(today);
  return { thisWeek: weekPlan(entries, start), nextWeek: weekPlan(entries, addCalendarDays(start, 7)) };
}

export const isEmptyWeek = (plan: WeekPlan): boolean => plan.starting.length + plan.leaving.length === 0;

/**
 * When the week that starts on `start` is told about the one after it: the
 * site's summary hour on the last working day of the week, because a Friday
 * afternoon mail is read on Monday, but a Thursday one is acted on.
 */
export function previewSendAt(start: IsoDate, hour: number, timeZone: string, calendar: Calendar): number {
  let day = addCalendarDays(start, 6);
  while (compareDates(day, start) > 0 && !isBusinessDay(day, calendar)) day = addCalendarDays(day, -1);
  return atTime(day, `${String(hour).padStart(2, "0")}:00`, timeZone);
}

/** The Monday of the week whose preview is next owed at `now`, given the last one sent (or null). */
export function nextPreview(
  today: IsoDate,
  sentFor: IsoDate | null,
  hour: number,
  timeZone: string,
  calendar: Calendar,
): { week: IsoDate; at: number } {
  let week = weekStart(today);
  // The current week's mail is owed unless it has gone; after that, the next week's.
  if (sentFor !== null && compareDates(sentFor, week) >= 0) week = addCalendarDays(sentFor, 7);
  return { week, at: previewSendAt(week, hour, timeZone, calendar) };
}

const NAMED = 15;
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mon 9 Nov". */
export function shortDay(date: IsoDate): string {
  return `${DAYS[weekday(date)]} ${Number(date.slice(8, 10))} ${MONTHS[Number(date.slice(5, 7)) - 1]}`;
}

function listOf(movers: readonly Mover[]): string {
  const shown = movers.slice(0, NAMED).map((mover) => `${mover.name} (${shortDay(mover.date)})`);
  const rest = movers.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", ");
}

/** The notice and the mail for one site's coming week: one sentence, every name. */
export function previewText(siteName: string, plan: WeekPlan): string {
  const parts = [
    plan.starting.length > 0 ? `starting ${listOf(plan.starting)}` : "",
    plan.leaving.length > 0 ? `leaving ${listOf(plan.leaving)}` : "",
  ].filter(Boolean);
  return `Next week at ${siteName} (from ${shortDay(plan.start)}): ${parts.join("; ")}.`;
}

/**
 * Who is told: the managers of the departments named, and whoever staffs each
 * of them at the site by default, because IT's manager and the person who
 * orders the laptops are not always one person. Nobody who is turned off.
 */
export function previewRecipients(
  members: readonly { id: string; role: string; department: string; active: boolean }[],
  staffed: Readonly<Record<string, { primary: string | null; backup: string | null }>>,
  departments: readonly string[],
): string[] {
  const told = new Set<string>();
  for (const department of departments) {
    for (const member of members)
      if (member.active && member.role === "manager" && member.department === department) told.add(member.id);
    const primary = staffed[department]?.primary;
    if (primary && members.some((member) => member.id === primary && member.active)) told.add(primary);
  }
  return [...told];
}
